import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import Navbar from '../components/Navbar';

const Home = () => {
  const { user } = useAuth();
  const [weeks, setWeeks] = useState([]);
  const [entryName, setEntryName] = useState('');
  const [picks, setPicks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedWeekNumber, setSubmittedWeekNumber] = useState(null);
  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    fetchAllWeeks();
    fetchAnnouncement();
  }, []);

  const fetchAnnouncement = async () => {
    try {
      const response = await api.get('/announcement');
      if (response.data && response.data.is_active) {
        setAnnouncement(response.data);
      }
    } catch (error) {
      console.error('Error fetching announcement:', error);
    }
  };

  const formatDeadlineDisplay = (deadlineStr) => {
    if (!deadlineStr) return 'Invalid';
    
    try {
      // Handle various date formats including ISO with microseconds
      let dateString = deadlineStr;
      
      // If it doesn't have timezone info, treat as HST (not UTC)
      // HST is UTC-10, so we need to add +10:00 to convert to UTC
      if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
        // The deadline is stored in HST format, so we add the HST offset (-10:00)
        dateString = dateString + '-10:00';
      }
      
      const dt = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(dt.getTime())) {
        console.error('Invalid date format:', deadlineStr);
        return 'Invalid Date';
      }
      
      const options = {
        timeZone: 'Pacific/Honolulu',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      return dt.toLocaleString('en-US', options);
    } catch (error) {
      console.error('Error formatting deadline:', deadlineStr, error);
      return 'Invalid Date';
    }
  };

  const fetchAllWeeks = async () => {
    try {
      // Force fresh data with timestamp - NO CACHE
      const timestamp = new Date().getTime();
      const response = await api.get(`/weeks?t=${timestamp}`);
      // Sort by week_number descending (most recent first)
      const sortedWeeks = response.data.sort((a, b) => b.week_number - a.week_number);
      setWeeks(sortedWeeks);
    } catch (error) {
      console.error('Error fetching weeks:', error);
      setMessage({ type: 'error', text: 'No weeks found' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePickTeam = (team) => {
    if (picks.includes(team)) {
      setPicks(picks.filter((t) => t !== team));
    } else if (picks.length < 6) {
      setPicks([...picks, team]);
    }
  };

  const handleSubmit = async (e, weekId, weekNumber) => {
    e.preventDefault();
    
    if (!entryName) {
      setMessage({ type: 'error', text: 'Please enter an entry name' });
      return;
    }
    
    if (picks.length !== 6) {
      setMessage({ type: 'error', text: 'Please select exactly 6 teams' });
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      await api.post(
        `/entries?user_id=${user?.id}&username=${user?.username}`,
        {
          week_id: weekId,
          entry_name: entryName,
          picks,
        }
      );
      setSubmittedWeekNumber(weekNumber);
      setShowSuccessModal(true);
      setEntryName('');
      setPicks([]);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to submit entry',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDeadlinePassed = (week) => {
    if (!week) return false;
    const deadlineStr = week.deadline.replace('T', 'T').replace(/Z?$/, '-10:00');
    const deadlineDate = new Date(deadlineStr);
    const now = new Date();
    return deadlineDate < now;
  };

  const getOppositeSpread = (spread) => {
    if (!spread) return '';
    const numericValue = parseFloat(spread);
    if (isNaN(numericValue)) return '';
    const opposite = -numericValue;
    return opposite > 0 ? `+${opposite}` : `${opposite}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Announcement Image */}
        {announcement && announcement.is_active && (announcement.image_data || announcement.image_url) && (
          <div className="mb-6 flex justify-center">
            <img 
              src={announcement.image_data || announcement.image_url} 
              alt="Announcement" 
              className="max-w-full h-auto rounded-lg shadow-lg"
            />
          </div>
        )}
        
        {/* Announcement Text */}
        {announcement && announcement.is_active && announcement.text && (
          <div className="mb-6 bg-dark-secondary rounded-lg p-4 text-center">
            <p className="text-gray-300 whitespace-pre-wrap">{announcement.text}</p>
          </div>
        )}

        <h1 className="text-3xl font-bold text-primary mb-6">All Weeks</h1>

        {weeks.length === 0 ? (
          <div className="bg-dark-secondary rounded-lg p-6 text-center">
            <p className="text-gray-300 text-xl">No weeks found</p>
          </div>
        ) : (
          <div className="space-y-6">
            {weeks.map((week) => {
              const deadlinePassed = isDeadlinePassed(week);
              return (
                <div key={week.id} className="bg-dark-secondary rounded-lg p-6">
                  <div className="mb-4">
                    <h2 className="text-2xl font-bold text-primary mb-2">
                      Week {week.week_number} - {week.season_year}
                    </h2>
                    <p className="text-gray-300">
                      Deadline: {formatDeadlineDisplay(week.deadline)} HST
                    </p>
                    {deadlinePassed && (
                      <p className="text-red-500 font-bold mt-2">
                        ⚠️ Deadline has passed - submissions closed
                      </p>
                    )}
                    {week.announcement && (
                      <div className="mt-4 bg-primary bg-opacity-10 border border-primary rounded-lg p-4">
                        <p className="text-primary font-semibold mb-1">📢 Announcement</p>
                        <p className="text-gray-300 whitespace-pre-wrap">{week.announcement}</p>
                      </div>
                    )}
                  </div>

                  {!deadlinePassed && (
                    <form onSubmit={(e) => handleSubmit(e, week.id, week.week_number)} className="mb-6">
                      <h3 className="text-xl font-bold text-white mb-4">Submit Entry</h3>
                      
                      <div className="mb-4">
                        <label className="block text-gray-300 mb-2">Entry Name</label>
                        <input
                          type="text"
                          value={entryName}
                          onChange={(e) => setEntryName(e.target.value)}
                          placeholder="Enter your entry name"
                          className="w-full bg-dark text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div className="mb-4">
                        <label className="block text-gray-300 mb-2">
                          Select 6 Teams ({picks.length}/6 selected)
                        </label>
                      </div>

                      {message.text && (
                        <div
                          className={`mb-4 px-4 py-3 rounded-lg ${
                            message.type === 'error'
                              ? 'bg-red-500/20 border border-red-500 text-red-500'
                              : 'bg-green-500/20 border border-green-500 text-green-500'
                          }`}
                        >
                          {message.text}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting || picks.length !== 6}
                        className="w-full bg-primary text-white py-3 rounded-lg font-bold hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Entry'}
                      </button>
                    </form>
                  )}

                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Games</h3>
                    <div className="space-y-3">
                      {week.games.map((game, index) => {
                        // Generate short abbreviations for over/under (first letter or two of each team)
                        const getShortAbbr = (team) => {
                          const words = team.split(' ');
                          if (words.length === 1) {
                            return team.substring(0, 1).toUpperCase();
                          }
                          // For multi-word teams like "LA RAMS", "GREEN BAY", etc.
                          return words.map(w => w[0]).join('').toUpperCase();
                        };
                        const team1Abbr = getShortAbbr(game.team1);
                        const team2Abbr = getShortAbbr(game.team2);
                        const gameAbbr = `${team1Abbr}/${team2Abbr}`;
                        const underPick = `${gameAbbr} under ${game.total}`;
                        const overPick = `${gameAbbr} over ${game.total}`;
                        
                        return (
                        <div key={index} className="bg-dark rounded-lg p-4">
                          {/* Team spread picks */}
                          <div className="flex items-center justify-between gap-3">
                            {deadlinePassed ? (
                              <>
                                <div className="flex-1 py-3 px-4 rounded-lg bg-dark-secondary text-gray-300 text-center font-semibold">
                                  {game.team1}
                                </div>
                                <span className="text-gray-400 font-bold text-sm">vs</span>
                                <div className="flex-1 py-3 px-4 rounded-lg bg-dark-secondary text-gray-300 text-center font-semibold">
                                  {game.team2}
                                </div>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handlePickTeam(game.team1)}
                                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition ${
                                    picks.includes(game.team1)
                                      ? 'bg-primary text-white'
                                      : 'bg-dark-secondary text-gray-300 hover:bg-gray-700'
                                  }`}
                                >
                                  {game.team1} {game.spread}
                                </button>
                                
                                <span className="text-gray-400 font-bold text-sm">vs</span>
                                
                                <button
                                  onClick={() => handlePickTeam(game.team2)}
                                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition ${
                                    picks.includes(game.team2)
                                      ? 'bg-primary text-white'
                                      : 'bg-dark-secondary text-gray-300 hover:bg-gray-700'
                                  }`}
                                >
                                  {game.team2} {getOppositeSpread(game.spread)}
                                </button>
                              </>
                            )}
                          </div>
                          
                          {/* Over/Under picks - only show if total is set */}
                          {game.total && (
                            <div className="flex items-center justify-between gap-3 mt-2">
                              {deadlinePassed ? (
                                <>
                                  <div className="flex-1 py-2 px-4 rounded-lg bg-dark-secondary text-gray-300 text-center font-semibold text-sm">
                                    {gameAbbr} under {game.total}
                                  </div>
                                  <span className="text-gray-400 font-bold text-xs">O/U</span>
                                  <div className="flex-1 py-2 px-4 rounded-lg bg-dark-secondary text-gray-300 text-center font-semibold text-sm">
                                    {gameAbbr} over {game.total}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handlePickTeam(underPick)}
                                    className={`flex-1 py-2 px-4 rounded-lg font-semibold transition text-sm ${
                                      picks.includes(underPick)
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-dark-secondary text-gray-300 hover:bg-gray-700'
                                    }`}
                                  >
                                    {gameAbbr} under {game.total}
                                  </button>
                                  
                                  <span className="text-gray-400 font-bold text-xs">O/U</span>
                                  
                                  <button
                                    onClick={() => handlePickTeam(overPick)}
                                    className={`flex-1 py-2 px-4 rounded-lg font-semibold transition text-sm ${
                                      picks.includes(overPick)
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-dark-secondary text-gray-300 hover:bg-gray-700'
                                    }`}
                                  >
                                    {gameAbbr} over {game.total}
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                          
                          {/* Player Props */}
                          {game.props && game.props.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-700">
                              <p className="text-xs text-gray-500 mb-2">Player Props</p>
                              <div className="space-y-2">
                                {game.props.map((prop, propIndex) => {
                                  const propTypeAbbr = prop.prop_type === 'pass yds' ? 'pass' : 
                                                       prop.prop_type === 'rush yds' ? 'rush' : 
                                                       prop.prop_type === 'rec yds' ? 'rec' :
                                                       prop.prop_type === '1H pts' ? '1H' : 'rec';
                                  const underPropPick = `${prop.player} u${prop.under_value} ${propTypeAbbr}`;
                                  const overPropPick = `${prop.player} o${prop.over_value} ${propTypeAbbr}`;
                                  
                                  return (
                                    <div key={propIndex} className="flex items-center justify-between gap-2">
                                      {deadlinePassed ? (
                                        <>
                                          <div className="flex-1 py-1 px-2 rounded bg-dark-secondary text-gray-300 text-center text-xs">
                                            {prop.player} u{prop.under_value} {propTypeAbbr}
                                          </div>
                                          <span className="text-gray-500 text-xs">{prop.prop_type}</span>
                                          <div className="flex-1 py-1 px-2 rounded bg-dark-secondary text-gray-300 text-center text-xs">
                                            {prop.player} o{prop.over_value} {propTypeAbbr}
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            onClick={() => handlePickTeam(underPropPick)}
                                            className={`flex-1 py-1 px-2 rounded font-medium transition text-xs ${
                                              picks.includes(underPropPick)
                                                ? 'bg-purple-600 text-white'
                                                : 'bg-dark-secondary text-gray-300 hover:bg-gray-700'
                                            }`}
                                          >
                                            {prop.player} u{prop.under_value} {propTypeAbbr}
                                          </button>
                                          <span className="text-gray-500 text-xs min-w-[50px] text-center">{prop.prop_type}</span>
                                          <button
                                            onClick={() => handlePickTeam(overPropPick)}
                                            className={`flex-1 py-1 px-2 rounded font-medium transition text-xs ${
                                              picks.includes(overPropPick)
                                                ? 'bg-purple-600 text-white'
                                                : 'bg-dark-secondary text-gray-300 hover:bg-gray-700'
                                            }`}
                                          >
                                            {prop.player} o{prop.over_value} {propTypeAbbr}
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-dark-secondary rounded-lg p-8 max-w-md w-full text-center">
            <div className="mb-4">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Entry Submitted!</h2>
              <p className="text-gray-300">
                Your entry has been successfully submitted for Week {submittedWeekNumber}.
              </p>
            </div>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full bg-primary text-white py-3 rounded-lg font-bold hover:bg-green-600 transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
